import { Link } from 'react-router-dom';
import { Zap, Shield, CheckCircle } from 'lucide-react';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';
import { FaqSection, faqSchema } from '@/components/FaqSection';
import { PackageJourney } from '@/sections/PackageJourney';
import { useReveal } from '@/lib/useReveal';
import { Section, Container, Display, Lead, Pill, CtaLink, Tile } from '@/components/system';

const faqs = [
  {
    question: '¿Cuánto cuesta usar MEC?',
    answer:
      'MEC es gratis durante tus primeros 6 meses — todas las funciones, todas las apps, todas tus sucursales. Después de los primeros 6 meses, $600 al año. Un solo plan, sin niveles ni sorpresas. Solicita una demo sin compromiso en la página Plataforma.',
  },
  {
    question: '¿Cuánto tarda la implementación?',
    answer:
      'Días, no meses. El equipo MEC configura tu cuenta, importa tus clientes y datos existentes, y te acompaña con un onboarding guiado hasta que tu agencia esté operando.',
  },
  {
    question: '¿Necesito conocimientos técnicos para usar la plataforma?',
    answer:
      'No. Mi Dashboard está diseñado para el personal de tu agencia, sin requisitos técnicos. Recibes onboarding guiado y soporte continuo del equipo MEC.',
  },
  {
    question: '¿Cómo usan mis clientes la app móvil?',
    answer:
      'Tus clientes descargan la app Mi Envío Cuba, eligen tu agencia y desde ese momento ven tus tarifas, tus sucursales y tus servicios. Todo lo que crean — envíos, solicitudes, mensajes — llega directo a tu panel.',
  },
  {
    question: '¿Puedo migrar los datos de mis clientes actuales?',
    answer:
      'Sí. Durante el onboarding importamos tus clientes y datos existentes para que no empieces de cero.',
  },
  {
    question: '¿Qué incluye la app de almacén y reparto?',
    answer:
      'Mi Admin: escaneo masivo de etiquetas QR en almacén, asignación de entregas por ruta y confirmación de cada entrega con foto, firma y GPS. Cada cambio de estado se refleja en tiempo real en Mi Dashboard y en la app de tus clientes.',
  },
  {
    question: '¿Funciona sin internet?',
    answer:
      'En campo, sí. Mi Admin escanea y confirma entregas sin señal y sincroniza todo al reconectar. Mi Dashboard y la app de tus clientes trabajan en la nube, en tiempo real.',
  },
  {
    question: '¿Sirve para una transitaria?',
    answer:
      'Sí. El escaneo masivo en almacén ya vive en Mi Admin, y Mi Cargo — en camino — recibe la carga de todas tus agencias, la consolida por destino, arma los manifiestos del contenedor y te permite facturar a cada agencia. Las transitarias entran con la Suite Empresarial: pregunta por ella en la página Plataforma.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Solicita una demo.',
    description:
      'Cuéntanos cómo opera tu agencia hoy. En una llamada o videollamada te mostramos toda la plataforma adaptada a tu operación actual.',
    details: ['Demo personalizada a tu tipo de agencia', 'Sin compromiso de contratación', 'Resolución de dudas en tiempo real'],
  },
  {
    number: '02',
    title: 'Activa Mi Dashboard y tus apps.',
    description:
      'Configuramos tu cuenta con tu información, tus clientes y tu operación, y tu agencia queda visible para tus clientes en la app Mi Envío Cuba.',
    details: ['Onboarding guiado por el equipo MEC', 'Importación de clientes y datos existentes', 'Tu agencia visible en la app Mi Envío Cuba'],
  },
  {
    number: '03',
    title: 'Opera, crece y escala.',
    description:
      'Tu equipo gestiona todo desde Mi Dashboard. Tus clientes rastrean desde su app. Tus repartidores confirman entregas en campo — incluso sin señal.',
    details: ['Control total desde un solo lugar', 'Actualizaciones en tiempo real en todas las apps', 'Soporte continuo del equipo MEC'],
  },
];

const whyItems = [
  { icon: Zap, title: 'Días, no meses.', text: 'Tu agencia puede estar operando en el sistema en días. Nosotros configuramos; tú apruebas.' },
  { icon: Shield, title: 'Datos seguros.', text: 'Datos cifrados en reposo, permisos granulares por empleado y auditoría inalterable.' },
  { icon: CheckCircle, title: 'Nunca solo.', text: 'El equipo MEC te acompaña en el onboarding y en cada etapa de crecimiento.' },
];

export function ComoFunciona() {
  const heroRef = useReveal<HTMLElement>();
  const stepsRef = useReveal<HTMLElement>();
  const whyRef = useReveal<HTMLElement>();

  return (
    <main className="bg-bg-light">
      <SEO
        title="Cómo Funciona MEC | Plataforma para Agencias de Envíos a Cuba"
        description="MEC en 3 pasos: solicita una demo, activa Mi Dashboard y tus apps, y empieza a operar. La plataforma de software para agencias de envíos a Cuba."
        path="/como-funciona"
        keywords="cómo funciona MEC, plataforma agencias envíos Cuba, software courier Cuba, onboarding agencia envíos, sistema gestión courier Cuba"
        jsonLd={faqSchema(faqs)}
      />

      {/* Hero */}
      <Section tone="white" ref={heroRef} className="pt-[120px] sm:pt-[150px] pb-16 sm:pb-20">
        <Container>
          <div className="mx-auto max-w-4xl text-center">
            <p data-reveal className="text-brand font-semibold text-base sm:text-lg mb-4">Cómo funciona</p>
            <Display data-reveal as="h1" size="xl">Tres pasos. Días, no meses.</Display>
            <Lead data-reveal className="mt-6 mx-auto max-w-2xl">
              De gestionar todo por WhatsApp a operar con Mi Dashboard, la app Mi Envío Cuba
              para tus clientes y Mi Admin para tu almacén y tus repartidores.
            </Lead>
            <div data-reveal className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              <Pill to="/plataforma#solicitar">Solicitar una demo</Pill>
              <CtaLink to="/plataforma">Ver la plataforma</CtaLink>
            </div>
          </div>
        </Container>
      </Section>

      {/* Steps — numbered rows */}
      <Section tone="grey" ref={stepsRef} className="py-16 sm:py-20">
        <Container>
          <div className="space-y-4">
            {steps.map((step) => (
              <div
                key={step.number}
                data-reveal
                className="rounded-[28px] bg-white p-8 sm:p-10 grid md:grid-cols-[120px_1fr] gap-6 md:gap-10 items-start"
              >
                <span className="font-heading text-6xl sm:text-7xl font-semibold tracking-[-0.04em] text-line select-none leading-none">
                  {step.number}
                </span>
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-3">{step.title}</h2>
                  <p className="text-text-secondary text-lg leading-relaxed max-w-2xl">{step.description}</p>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {step.details.map((d) => (
                      <li key={d} className="inline-flex items-center gap-2 rounded-full bg-bg-light px-4 py-2 text-sm font-medium text-text-secondary">
                        <CheckCircle className="w-4 h-4 text-brand" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* The package's journey */}
      <PackageJourney />

      {/* Why */}
      <Section tone="grey" ref={whyRef} className="py-16 sm:py-20">
        <Container>
          <Display data-reveal size="md" className="mb-10">¿Por qué las agencias eligen MEC?</Display>
          <div className="grid sm:grid-cols-3 gap-4">
            {whyItems.map((w) => (
              <Tile key={w.title} data-reveal className="p-7 sm:p-8">
                <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center">
                  <w.icon className="w-5 h-5 text-brand" />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">{w.title}</h3>
                <p className="mt-2 text-text-secondary leading-relaxed">{w.text}</p>
              </Tile>
            ))}
          </div>
          <div data-reveal className="mt-10 text-center">
            <p className="text-text-secondary">
              ¿Listo? <Link to="/plataforma#solicitar" className="text-brand font-medium hover:underline">Solicita una demo</Link> — sin compromiso.
            </p>
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section tone="white" className="py-16 sm:py-20 pb-0">
        <FaqSection faqs={faqs} />
      </Section>

      <Footer />
    </main>
  );
}
